import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import api from '../lib/api';
import ProductCard from '../components/ProductCard';
import ProductSkeleton from '../components/ProductSkeleton';
import { SparkleField } from '../components/SparkleField';
import SectionDivider from '../components/SectionDivider';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useSettings } from '../context/SettingsContext';
import {
  Sparkles, Coffee, GlassWater, Shirt, Cookie, Footprints, Backpack,
  Droplets, Heart, Gem, Gift, Star, MessageCircle, Wallet, HandCoins, CreditCard,
  Truck, ShieldCheck, PackageCheck, Crown, Wand2, Cake, Music, Camera, Smile, Flower2, Palette,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Reveal } from '../components/Reveal';

const ICONS = {
  Sparkles, Coffee, GlassWater, Shirt, Cookie, Footprints, Backpack, Droplets,
  Heart, Gem, Gift, Star, Crown, Wand2, Cake, Music, Camera, Smile, Flower2, Palette,
};

const PAGE_SIZE = 12;
// Caché en memoria (5 min) — al regresar al catálogo desde detalle, repinta instantáneo
const productCache = { all: null, byCat: new Map(), ts: 0 };
const CACHE_TTL_MS = 5 * 60 * 1000;

export default function Home() {
  const { settings } = useSettings();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);

  // Carga estática (categorías, testimonios, FAQs) sólo una vez
  useEffect(() => {
    Promise.all([
      api.get('/categories').then((r) => setCategories(r.data)),
      api.get('/testimonials').then((r) => setTestimonials(r.data)),
      api.get('/faqs').then((r) => setFaqs(r.data)),
    ]).catch(() => {});
  }, []);

  // Carga productos paginados; resetea al cambiar de categoría
  const fetchPage = useCallback(async (pageNum, category, append) => {
    const cacheKey = category === 'all' ? 'all' : category;
    const now = Date.now();
    // Cache solo para la primera página
    if (pageNum === 1 && !append) {
      const cached = cacheKey === 'all' ? productCache.all : productCache.byCat.get(cacheKey);
      if (cached && now - productCache.ts < CACHE_TTL_MS) {
        setProducts(cached.data);
        setTotalPages(cached.totalPages);
        setLoadingProducts(false);
        return;
      }
    }
    if (append) setLoadingMore(true); else setLoadingProducts(true);
    try {
      const params = { page: pageNum, limit: PAGE_SIZE };
      if (category !== 'all') params.category = category;
      const { data } = await api.get('/products', { params });
      const list = data.data || [];
      setTotalPages(data.totalPages || 1);
      setProducts((prev) => (append ? [...prev, ...list] : list));
      if (pageNum === 1) {
        const entry = { data: list, totalPages: data.totalPages || 1 };
        if (cacheKey === 'all') productCache.all = entry;
        else productCache.byCat.set(cacheKey, entry);
        productCache.ts = now;
      }
    } catch {
      if (!append) setProducts([]);
    } finally {
      setLoadingProducts(false);
      setLoadingMore(false);
    }
  }, []);

  // Al montar y al cambiar categoría: reset y trae página 1
  useEffect(() => {
    setPage(1);
    fetchPage(1, activeCategory, false);
  }, [activeCategory, fetchPage]);

  // Scroll infinito vía IntersectionObserver
  useEffect(() => {
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && page < totalPages && !loadingMore && !loadingProducts) {
        const next = page + 1;
        setPage(next);
        fetchPage(next, activeCategory, true);
      }
    }, { rootMargin: '400px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [page, totalPages, loadingMore, loadingProducts, activeCategory, fetchPage]);

  const waNumber = (settings.whatsapp_number || '').replace(/\D/g, '');
  // Memoizados para evitar recálculo en cada render
  const featured = useMemo(() => products.slice(0, 4), [products]);
  const filtered = useMemo(
    () => activeCategory === 'all' ? products : products.filter((p) => p.category === activeCategory),
    [products, activeCategory]
  );

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section id="inicio" className="relative overflow-hidden hero-radial">
        <SparkleField mobileCount={1} desktopCount={2} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-28 grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/80 backdrop-blur border border-pinky-200 text-xs font-bold uppercase tracking-[0.18em] text-pinky-600">
              <Sparkles className="h-3.5 w-3.5" /> {settings.hero_subtitle || 'Entrega inmediata en Guatemala 🇬🇹'}
            </span>
            <h1 data-testid="hero-title" className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-slate-900 mt-5">
              <span className="shimmer-text">{settings.slogan || 'Gifts siempre contigo'}</span>
            </h1>
            <p className="mt-5 sm:mt-6 text-base sm:text-lg text-slate-600 max-w-lg leading-relaxed">
              Productos mágicos importados desde USA, Disney y los parques temáticos más queridos. Listos para hacer feliz a alguien especial hoy mismo 💖
            </p>
            <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row sm:flex-wrap gap-3">
              <Button
                data-testid="hero-cta-catalog"
                onClick={() => scrollTo('productos')}
                size="lg"
                className="btn-pink rounded-full h-14 px-8 text-base font-bold pink-glow w-full sm:w-auto"
              >
                Ver catálogo <Sparkles className="h-5 w-5 ml-1" />
              </Button>
              <Button
                data-testid="hero-cta-whatsapp"
                asChild
                size="lg"
                className="btn-pink-outline rounded-full h-14 px-8 text-base font-bold w-full sm:w-auto"
              >
                <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noreferrer">
                  <MessageCircle className="h-5 w-5 mr-1" /> Escríbenos
                </a>
              </Button>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {[
                { icon: Truck, label: 'Entrega inmediata' },
                { icon: ShieldCheck, label: 'Productos originales' },
                { icon: PackageCheck, label: 'Calidad premium' },
              ].map((f) => (
                <div key={f.label} className="text-center">
                  <div className="mx-auto h-11 w-11 rounded-full bg-white border border-pinky-100 flex items-center justify-center text-pinky-500 pink-glow mb-2">
                    <f.icon className="h-5 w-5" strokeWidth={1.8} />
                  </div>
                  <p className="text-xs font-semibold text-slate-600">{f.label}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative aspect-square max-w-xs sm:max-w-md lg:max-w-lg mx-auto">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-pinky-200/60 to-lilac-200/60 blur-3xl" />
              {/* Marco arcoíris pastel decorativo */}
              <div className="absolute inset-0 castle-rainbow-frame opacity-50" aria-hidden="true" />
              {settings.logo_url ? (
                <img
                  src={settings.logo_url}
                  alt="Gifts GT"
                  className="relative w-full h-full object-contain animate-float-slow rounded-full"
                  onError={(e) => {
                    // Si falla la carga, ocultar el <img> y mostrar el fallback hermano
                    e.currentTarget.style.display = 'none';
                    const sib = e.currentTarget.nextElementSibling;
                    if (sib) sib.style.display = 'flex';
                  }}
                />
              ) : null}
              <div
                className="relative w-full h-full rounded-full bg-gradient-to-br from-pinky-100 via-pinky-200 to-lilac-200 items-center justify-center"
                style={{ display: settings.logo_url ? 'none' : 'flex' }}
              >
                <Gift className="h-40 w-40 text-pinky-400" strokeWidth={1.2} />
              </div>
              {/* 4 destellos mágicos SVG distribuidos alrededor del castillo */}
              <svg className="absolute top-[6%] right-[8%] w-4 h-4 sm:w-5 sm:h-5 hero-spark text-pinky-400 pointer-events-none" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2l1.5 7L20 10.5l-6.5 1.5L12 19l-1.5-6L4 10.5 10.5 9z"/>
              </svg>
              <svg className="absolute top-[14%] left-[4%] w-3 h-3 sm:w-4 sm:h-4 hero-spark text-amber-300 pointer-events-none" style={{ animationDelay: '0.9s' }} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2l1.5 7L20 10.5l-6.5 1.5L12 19l-1.5-6L4 10.5 10.5 9z"/>
              </svg>
              <svg className="absolute bottom-[10%] right-[12%] w-3 h-3 sm:w-4 sm:h-4 hero-spark text-lilac-400 pointer-events-none" style={{ animationDelay: '1.8s' }} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2l1.5 7L20 10.5l-6.5 1.5L12 19l-1.5-6L4 10.5 10.5 9z"/>
              </svg>
              <svg className="absolute bottom-[18%] left-[8%] w-3.5 h-3.5 sm:w-5 sm:h-5 hero-spark text-pinky-300 pointer-events-none hidden sm:block" style={{ animationDelay: '2.4s' }} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2l1.5 7L20 10.5l-6.5 1.5L12 19l-1.5-6L4 10.5 10.5 9z"/>
              </svg>
            </div>
          </motion.div>
        </div>
      </section>

      <SectionDivider />

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 dot-pattern-bg rounded-3xl">
        <Reveal>
          <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-pinky-500">✨ Disponibles ahora</span>
              <h2 className="font-display text-3xl sm:text-4xl text-slate-900 mt-2">Destacados de la semana</h2>
            </div>
            <Button onClick={() => scrollTo('productos')} variant="ghost" className="rounded-full text-pinky-600 hover:bg-pinky-50 font-semibold" data-testid="view-all-btn">
              Ver todo →
            </Button>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
          {featured.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 0.08} className="h-full">
              <ProductCard product={p} />
            </Reveal>
          ))}
        </div>
      </section>

      <SectionDivider />

      {/* CATEGORIES */}
      <section id="categorias" className="py-16 bg-white border-y border-pinky-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-10">
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-pinky-500">Explora</span>
              <h2 className="font-display text-3xl sm:text-4xl text-slate-900 mt-2">Nuestras Categorías</h2>
              <p className="mt-3 text-slate-600 max-w-xl mx-auto">Encuentra tu favorito entre nuestras 10 categorías mágicas</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {categories.map((c, idx) => {
              const Icon = ICONS[c.icon] || Gift;
              return (
                <Reveal key={c.slug} delay={(idx % 5) * 0.06} className="h-full">
                  <button
                    data-testid={`category-${c.slug}`}
                    onClick={() => { setActiveCategory(c.slug); scrollTo('productos'); }}
                    className="group soft-card p-5 flex flex-col items-center text-center w-full h-full"
                  >
                    <div className="h-16 w-16 rounded-2xl bg-pinky-50 flex items-center justify-center text-pinky-500 group-hover:bg-pinky-100 group-hover:scale-110 transition-all">
                      <Icon className="h-8 w-8" strokeWidth={1.5} />
                    </div>
                    <p className="mt-3 font-semibold text-slate-800 text-sm">{c.name}</p>
                  </button>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section id="productos" className="py-16 relative">
        <SparkleField mobileCount={1} desktopCount={1} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="mb-8 text-center">
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-pinky-500">Catálogo</span>
              <h2 className="font-display text-3xl sm:text-4xl text-slate-900 mt-2">Todos nuestros productos</h2>
            </div>
          </Reveal>

          <div className="flex gap-2 mb-8 overflow-x-auto no-scrollbar -mx-1 px-1 md:flex-wrap md:justify-center md:overflow-visible" data-testid="category-filter">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap shrink-0 ${
                activeCategory === 'all' ? 'bg-pinky-500 text-white pink-glow' : 'bg-white border border-pinky-200 text-slate-700 hover:bg-pinky-50'
              }`}
              data-testid="filter-all"
            >
              Todos
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                onClick={() => setActiveCategory(c.slug)}
                data-testid={`filter-${c.slug}`}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap shrink-0 ${
                  activeCategory === c.slug ? 'bg-pinky-500 text-white pink-glow' : 'bg-white border border-pinky-200 text-slate-700 hover:bg-pinky-50'
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>

          {loadingProducts ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="products-skeleton">
              {Array.from({ length: PAGE_SIZE }).map((_, i) => (
                <ProductSkeleton key={`skeleton-${i}`} />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <Sparkles className="h-12 w-12 mx-auto text-pinky-300 mb-3" />
              <p>No hay productos en esta categoría todavía.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="products-grid">
                {products.map((p, i) => (
                  <Reveal key={p.id} delay={(i % 4) * 0.06} className="h-full">
                    <ProductCard product={p} />
                  </Reveal>
                ))}
              </div>
              {/* Sentinel para scroll infinito */}
              {page < totalPages && (
                <div ref={sentinelRef} className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="products-load-more">
                  {Array.from({ length: Math.min(PAGE_SIZE, 4) }).map((_, i) => (
                    <ProductSkeleton key={`more-skeleton-${i}`} />
                  ))}
                </div>
              )}
              {page >= totalPages && products.length > PAGE_SIZE && (
                <p className="mt-10 text-center text-sm text-slate-400" data-testid="products-end">
                  ✨ Has visto todos nuestros productos ({products.length})
                </p>
              )}
            </>
          )}
        </div>
      </section>

      <SectionDivider />

      {/* PAYMENT METHODS */}
      <section id="pagos" className="py-16 bg-pinky-50 border-y border-pinky-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-10">
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-pinky-500">Formas de pago</span>
              <h2 className="font-display text-3xl sm:text-4xl text-slate-900 mt-2">Métodos aceptados</h2>
              <p className="mt-3 text-slate-600 max-w-xl mx-auto">Coordinamos todo por WhatsApp para tu comodidad.</p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: Wallet, title: 'Depósito bancario', desc: 'Te enviamos los datos por WhatsApp para que realices tu depósito o transferencia.' },
              { icon: HandCoins, title: 'Pago contra entrega', desc: 'Paga en efectivo al momento de recibir tu pedido en la puerta de tu casa.', badge: 'Popular' },
              { icon: CreditCard, title: 'Visa Link', desc: 'Recibe un link seguro para pagar con tu tarjeta Visa desde tu celular.' },
            ].map((m, i) => (
              <Reveal key={m.title} delay={i * 0.1} className="h-full">
                <div data-testid={`payment-${i}`} className="soft-card p-7 relative h-full">
                  {m.badge && <span className="absolute -top-3 right-5 px-3 py-1 rounded-full bg-pinky-500 text-white text-xs font-bold">{m.badge}</span>}
                  <div className="h-14 w-14 rounded-2xl bg-pinky-100 text-pinky-600 flex items-center justify-center mb-4">
                    <m.icon className="h-7 w-7" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-xl text-slate-900">{m.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* TESTIMONIALS */}
      <section id="opiniones" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-pinky-500">Opiniones</span>
            <h2 className="font-display text-3xl sm:text-4xl text-slate-900 mt-2">Lo que dicen nuestras clientas</h2>
            <div className="mt-3 inline-flex items-center gap-1 text-amber-400">
              {[...Array(5)].map((_, i) => <Star key={`hero-rating-${i}`} className="h-5 w-5 fill-amber-400" />)}
              <span className="ml-2 text-sm font-semibold text-slate-600">5.0 basado en 500+ pedidos felices</span>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.map((t, idx) => (
              <motion.div
                key={t.id}
                data-testid={`testimonial-${idx}`}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
                className="soft-card p-6"
              >
                <div className="flex items-center gap-1 text-amber-400 mb-3">
                  {[...Array(t.rating || 5)].map((_, i) => <Star key={`t-${t.id}-${i}`} className="h-4 w-4 fill-amber-400" />)}
                </div>
                <p className="text-slate-700 leading-relaxed italic">"{t.message}"</p>
                <div className="mt-5 flex items-center gap-3 pt-4 border-t border-pinky-100">
                  {t.avatar && <img src={t.avatar} alt={t.name} className="h-10 w-10 rounded-full object-cover" />}
                  <div>
                    <p className="font-bold text-slate-800 text-sm">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.location}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* FAQ */}
      <section id="faq" className="py-16 bg-white border-y border-pinky-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-10">
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-pinky-500">Preguntas frecuentes</span>
              <h2 className="font-display text-3xl sm:text-4xl text-slate-900 mt-2">¿Tienes dudas?</h2>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
              {faqs.map((f) => (
                <AccordionItem key={f.id} value={f.id} className="soft-card px-5 border-none">
                  <AccordionTrigger className="text-left font-semibold text-slate-800 hover:no-underline py-5" data-testid={`faq-q-${f.id}`}>
                    {f.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-5">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <SectionDivider />

      {/* CONTACT */}
      <section id="contacto" className="relative py-20 overflow-hidden">
        <SparkleField mobileCount={1} desktopCount={1} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-pinky-500">Contacto</span>
          <h2 className="font-display text-4xl sm:text-5xl text-slate-900 mt-2">
            ¿Lista para pedir? <span className="shimmer-text">Escríbenos</span>
          </h2>
          <p className="mt-4 text-slate-600 max-w-xl mx-auto">
            Coordinamos todo por WhatsApp: disponibilidad, fotos extra, pago y entrega. Te atendemos con cariño 💖
          </p>
          <div className="mt-8 inline-flex flex-col sm:flex-row gap-3">
            <Button
              asChild
              size="lg"
              data-testid="contact-whatsapp-btn"
              className="btn-emerald rounded-full h-14 px-8 font-bold text-base pink-glow"
            >
              <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noreferrer">
                <MessageCircle className="h-5 w-5 mr-2" /> {settings.whatsapp_number}
              </a>
            </Button>
          </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
